import { useQueryClient } from '@tanstack/react-query';
import Cookie from 'js-cookie';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';

import { Routes } from '@common/constants';
import { parseSpaEntityData } from '@common/components/SpaEntity/SpaEntityContainer';
import { getSpaFacilityTimeslots } from '@features/spa/hooks/useGetSpaFacilityTimeslots';
import { useReturnToTimeWhenSlotTaken } from '@features/spa/hooks/useReturnToTimeWhenSlotTaken';
import { slotSeats } from '@features/spa/hooks/useTimeslotAvailability';
import { findSlotForArrivalTime } from '@features/spa/utils/resolveSelectedSlotPricing';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { SpaEntityType } from 'shared-types/Spa';

import { FacilityFormValues } from '../FacilityBookingV2.types';

type UseSubmitFacilityParams = {
  form: UseFormReturn<FacilityFormValues>;
  spaEntity: SpaEntityType | undefined;
  propertyId: number;
  language: string;
  id: number;
  onClose: (showMiniBasket: boolean) => void;
  hasExtras: boolean;
  onInvalid?: () => void;
};

export function useSubmitFacility({
  form,
  spaEntity,
  propertyId,
  language,
  id,
  onClose,
  onInvalid,
}: UseSubmitFacilityParams) {
  const dispatch = useDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const currentBasketItems = useSelector(
    (state: Record<string, unknown>) =>
      (basketSelectors.getBasketDetails(state) as { items?: unknown[] })?.items || []
  );

  const isStillOffered = useCallback(
    async (date: string, arrivalTime: NonNullable<FacilityFormValues['arrivalTime']>) => {
      const slots = await getSpaFacilityTimeslots({
        spaFacilityId: id,
        startDate: date,
        language,
        propertyId,
        numberOfDays: 1,
        numberOfGuests: 1,
      });
      const slot = findSlotForArrivalTime(slots, arrivalTime);
      return !!slot && slotSeats(slot, form.getValues('guests')?.length);
    },
    [id, language, propertyId, form]
  );

  const returnToTimeIfSlotTaken = useReturnToTimeWhenSlotTaken({
    form: form as never,
    id,
    bookingRoute: Routes.FACILITY_BOOKING,
    isStillOffered,
  });

  const addToBasket = useCallback(
    (data: FacilityFormValues) => {
      if (!spaEntity) return;
      const parsedEntity = parseSpaEntityData(data as any, spaEntity, 'spa-facilities');
      parsedEntity.guestDetails = parsedEntity.guestDetails.map((guest: any, i: number) => {
        const formGuest = data.guests[i];
        if (formGuest?.loggedInUser) {
          const { id: _id, ...rest } = guest;
          return { ...rest, loggedInUser: formGuest.loggedInUser };
        }
        return guest;
      });
      const basketId = Cookie.get('basketId');

      const promise = basketId
        ? dispatch(
            updateBasket(
              propertyId,
              basketId,
              language,
              {
                items: [...currentBasketItems, parsedEntity],
              },
              false,
              true
            ) as any
          )
        : dispatch(
            createBasket(
              propertyId,
              language,
              {
                items: [parsedEntity],
              },
              true
            ) as any
          );

      Promise.resolve(promise)
        .then(() => onClose(true))
        .catch(() => returnToTimeIfSlotTaken())
        .finally(() =>
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey.includes('spaFacility') && query.queryKey.includes('timeslots'),
          })
        );
    },
    [
      spaEntity,
      currentBasketItems,
      dispatch,
      propertyId,
      language,
      queryClient,
      onClose,
      returnToTimeIfSlotTaken,
    ]
  );

  const handleSubmit = form.handleSubmit(addToBasket as any, onInvalid);

  const handleNextStep = form.handleSubmit(() => {
    router.push({
      pathname: Routes.FACILITY_BOOKING,
      query: { id, bookingStep: 'extras' },
    });
  }, onInvalid);

  const handleBack = useCallback(() => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
  }, [router, id]);

  return {
    handleSubmit,
    handleNextStep,
    handleBack,
  };
}
